import maplibregl, {Marker, Popup} from 'maplibre-gl';
import * as React from "react";
import {ReactElement, useEffect, useRef, useState} from "react";
import 'maplibre-gl/dist/maplibre-gl.css';
import './CustomMapLibre.css';
import {ArrowRight, Fullscreen, Layers, Place} from "@mui/icons-material";
import {mapMarkerContainer} from "./MapMarker";
import ReactDOMServer from "react-dom/server";

declare global {
    interface HTMLElement {
        webkitRequestFullscreen?: () => Promise<void>;
    }
}

interface MapProps {
    mapMarkers?: MapMarker[];
    center?: MapLatLon;
    minHeight?: string;
    maxHeight?: string;
    height?: string;
    defaultZoom?: number;
    minZoom?: number;
    maxZoom?: number;
    legend?: MapLegend[];
    openPopupOnStart?: boolean;
    showLocationSearch?: boolean;
    minLabelOccurrenceForLabelFilter?: { category: string, minOccurrence: number }[];
    showBatchLayerButtonsIfMoreLabelsThan?: number;
}

export interface MapLatLon {
    lng: number;
    lat: number;
}

export interface MapMarkerLink {
    label: string;
    url: string;
    icon?: string;
}

export interface MapMarker extends MapLatLon {
    title: string;
    subtitle?: string;
    content: string[];
    label?: MapLabel[],
    icons?: MapMarkerIcon[],
    links?: MapMarkerLink[],
    image?: string;
}

interface MapLabel {
    title: string;
    category: string,
}

interface MapUniqueLabel extends MapLabel {
    enabled: boolean;
    occurrence?: number;
    visible?: boolean;
}

export interface MapLegend {
    label: string;
    style: HTMLElement;
}

export interface MapMarkerIcon {
    label: MapLabel[];
    style: HTMLElement;
    visible: boolean;
}

export function Map(props: MapProps) {
    const mapRef = useRef<maplibregl.Map | null>(null); // Referenz auf die Karteninstanz
    const markersRef = useRef<Marker[]>([]); // Referenz auf die Marker auf der Karte
    const [uniqueLabelsSorted, setUniqueLabelsSorted] = useState<MapUniqueLabel[]>([]); // Eindeutige Labels
    const [disabledLabels, setDisabledLabels] = useState<MapLabel[]>([]); // Deaktivierte Labels
    const [showLayer, setShowLayer] = useState<boolean>(false); // Zustand zum Ein-/Ausblenden der Ebene
    const [mapKey] = useState('map' + Math.random().toString(36).substring(7));
    const [firstPopupOpenedOnStart, setFirstPopupOpenedOnStart] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Initialisiere die Karte nach einer kurzen Verzögerung
        setTimeout(() => {

            //isMobile Device? Also check android or iphone`
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

            mapRef.current = new maplibregl.Map({
                container: mapKey, // Container-ID
                style: 'https://osrm.erl.de/styles/erl/style.json', // OSRM Style URL
                center: [props.center.lng, props.center.lat], // Startposition [lng, lat]
                zoom: props.defaultZoom, // Start-Zoom
                minZoom: props.minZoom,
                maxZoom: props.maxZoom,
                cooperativeGestures: isMobile,
                locale: {
                    //in German
                    'CooperativeGesturesHandler.WindowsHelpText': 'Verwende Strg + Scrollen, um zu scrollen und zu zoomen.',
                    'CooperativeGesturesHandler.MacHelpText': 'Verwende \u2318 + Scrollen, um zu scrollen und zu zoomen.',
                    'CooperativeGesturesHandler.MobileHelpText': 'Verwende zwei Finger, um zu scrollen und zu zoomen.',
                },
            });

            // Füge einen Zoom-Event-Listener hinzu, um die Icon-Größen anzupassen
            mapRef.current.on('zoom', () => {
                zoomIcons();
            });

        }, 10);

    }, []);

    const escapeHtml = (unsafe: string): string => {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    useEffect(() => {
        // Aktualisiere die Marker, wenn sich mapMarkers oder disabledLabels ändern
        setTimeout(() => {
            markersRef.current.forEach(marker => marker.remove()); // Entferne vorhandene Marker
            markersRef.current = [];

            getMapIconsWithoutDisabledLabels().map((marker: MapMarker) => {
                let labels = marker.label || [];
                marker.icons?.map(icon => {
                    labels = labels.concat(icon.label);
                });

                // Sichtbare Icons abrufen
                const visibleIcons = marker.icons?.filter(icon => icon.visible);

                if (visibleIcons) {

                    // Erstelle ein Popup für den Marker
                    const popup = new Popup({
                        anchor: 'bottom',
                        maxWidth: '280px',
                        subpixelPositioning: true,
                    }).setHTML('<div class="popup-content-container"></div>').on('open', () => {
                        setTimeout(() => {

                            let subtitle = '';
                            if (marker.subtitle) {
                                subtitle = '<h4 class="maplibregl-h4">' + marker.subtitle + '</h4>';
                            }

                            let links = '';
                            if (marker.links) {
                                links = '<div class="maplibregl-links">' + marker.links.map(link => {

                                    let icon = <ArrowRight/>;
                                    if (link.icon) {
                                        icon = <Place/>;
                                    }

                                    return '<a class="maplibregl-link" href="' + link.url + '" target="_blank">' + ReactDOMServer.renderToString(icon) + link.label + '</a>'
                                }).join('') + '</div>';
                            }

                            let image = '';
                            if (marker.image) {
                                image = '<img loading="lazy" alt="" class="maplibregl-image" src="' + marker.image + '" alt="' + marker.title + '" />';
                            }

                            // Your callback code to update the HTML
                            const popupContent = containerRef.current.querySelector('.maplibregl-popup-content .popup-content-container');
                            if (popupContent) {
                                popupContent.innerHTML = image + subtitle +
                                    '<h3 class="maplibregl-h3">' + marker.title + '</h3>' +
                                    '<div class="maplibregl-content">' + marker.content.join('<br>') + '</div>' + links +
                                    (labels ? '<div class="maplibregl-labels">' + labels.map(label => '<div class="maplibregl-label" title="' + escapeHtml(label.title) + '">' + escapeHtml(label.title) + '</div>').join('') + '</div>' : '')
                            }

                        }, 10);
                    }).addTo(mapRef.current);

                    // Erstelle und füge den Marker zur Karte hinzu
                    const newMarker = new Marker({element: mapMarkerContainer(visibleIcons.map(icon => icon.style))})
                        .setLngLat([marker.lng, marker.lat])
                        .setPopup(popup)
                        .addTo(mapRef.current);

                    markersRef.current.push(newMarker);
                }
            });

            if (props.openPopupOnStart && !firstPopupOpenedOnStart) {
                setTimeout(() => {
                    markersRef.current[0].togglePopup();
                    setFirstPopupOpenedOnStart(true);
                }, 1000);
            }

            zoomIcons();
        }, 20);

    }, [props.mapMarkers, disabledLabels]);

    useEffect(() => {
        // Aktualisiere die eindeutigen Labels, wenn sich mapMarkers ändern
        buildLabelsSorted();
    }, [props.mapMarkers]);

    const zoomIcons = () => {
        // Passe die Icon-Größen basierend auf der Zoomstufe an
        const zoom = mapRef.current.getZoom();
        const size = Math.min(20, Math.max(10, 15 * (zoom / 8))); // Größe basierend auf Zoomstufe anpassen

        markersRef.current.forEach(marker => {
            const el = marker.getElement();
            el.style.height = `${size}px`;
        });
    }

    const buildLabelsSorted = () => {
        // Baue und sortiere eindeutige Labels
        let allLabels: MapLabel[] = [];

        props.mapMarkers.map((marker: MapMarker) => {
            allLabels = allLabels.concat(marker.label || []);

            marker.icons?.map(icon => {
                allLabels = allLabels.concat(icon.label);
            });
        });

        //set uniqueLabelsSorted. Make label.label unique and sort by occurrence
        let uniqueLabelsSortedNew: MapUniqueLabel[] = [];
        allLabels.forEach(label => {
            let index = uniqueLabelsSortedNew.findIndex(item => item.title === label.title && item.category === label.category);
            if (index === -1) {
                uniqueLabelsSortedNew.push({title: label.title, enabled: true, occurrence: 1, visible: true, category: label.category});
            } else {
                uniqueLabelsSortedNew[index].occurrence++;
            }
        });

        //set all invisible with occurrence less than 2
        uniqueLabelsSortedNew.forEach(item => {
            props.minLabelOccurrenceForLabelFilter?.map(minOccurrence => {
                if (minOccurrence.category == item.category && item.occurrence < minOccurrence.minOccurrence) {
                    item.visible = false;
                }
            });

        });

        const invisibleLabels = uniqueLabelsSortedNew.filter(item => !item.visible);
        //get unique categories from invisibleLabels
        const invisibleCategories = invisibleLabels.map(item => item.category).filter((value, index, self) => self.indexOf(value) === index);

        //count occurrence of invisible categories
        const invisibleCategoriesOccurrence = invisibleCategories.map(category => {
            return invisibleLabels.filter(item => item.category === category).reduce((acc, item) => acc + item.occurrence, 0);
        });
        const invisibleCategoriesOccurrenceSum = invisibleCategoriesOccurrence.reduce((acc, item) => acc + item, 0);

        //if there are some invisible items add label "Sonstige"
        invisibleCategories.map(category => {
            uniqueLabelsSortedNew.push({title: 'Sonstige', enabled: true, occurrence: invisibleCategoriesOccurrenceSum, visible: true, category: category});
        });

        setUniqueLabelsSorted(uniqueLabelsSortedNew);

    }

    const compareLabels = (MapLabel1: MapLabel, MapLabel2: MapLabel): boolean => {
        return MapLabel1.title === MapLabel2.title && MapLabel1.category === MapLabel2.category;
    }

    const arrayContainsLabel = (label: MapLabel, array: MapLabel[],): boolean => {
        return array.some(item => compareLabels(item, label));
    }

    const getMapIconsWithoutDisabledLabels = () => {
        // Filtere Kartenmarker, um diejenigen mit deaktivierten Labels auszuschließen
        return props.mapMarkers.filter(marker => {
            let result = !marker.label || marker.label.every(label => !arrayContainsLabel(label, disabledLabels));
            // Setze Icons sichtbar oder unsichtbar, wenn Labels deaktiviert sind
            marker.icons?.map(icon => {
                icon.visible = icon.label.every(label => !arrayContainsLabel(label, disabledLabels));
            });
            return result;
        });
    }

    const createReactElementFromHTMLElement = (htmlElement: HTMLElement): ReactElement => {
        // Erstelle ein React-Element aus einem HTML-Element
        return React.createElement('div', {
            dangerouslySetInnerHTML: {__html: htmlElement.outerHTML}
        });
    }

    const batchChangeLabelStatus = (enabled: boolean, category: string) => {
        let uniqueLabelsSortedNew = uniqueLabelsSorted.map(item => {

            if (item.category !== category) {
                return item;
            }

            return {
                title: item.title,
                enabled: enabled,
                visible: item.visible,
                occurrence: item.occurrence,
                category: item.category,
            };
        })
        setUniqueLabelsSorted(uniqueLabelsSortedNew);
        setDisabledLabels(uniqueLabelsSortedNew.filter(item => !item.enabled).map(item => item));
        return false;
    }

    const getUniqueLabelsSortedByCategoryAndVisible = (category: string) => {
        let labels = uniqueLabelsSorted.filter(item => item.category === category && item.visible);
        labels.sort((a, b) => a.title.localeCompare(b.title));
        return labels;
    }

    const getUniqueAndVisibleLabelCategories = () => {
        let categories = uniqueLabelsSorted.filter(item => item.visible).map(item => item.category);
        return categories.filter((value, index, self) => self.indexOf(value) === index);
    }

    const switchFullscreen = () => {

        if (!fullscreen) {
            const elem = containerRef.current;
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.webkitRequestFullscreen) { /* Safari */
                elem.webkitRequestFullscreen();
            }
            setFullscreen(true);
            mapRef.current.setMinZoom(props.minZoom + 1);
        } else {
            document.exitFullscreen();
            mapRef.current.setMinZoom(props.minZoom);
            setFullscreen(false);
        }

        document.onfullscreenchange = () => {
            const isFullscreen = document.fullscreenElement !== null;
            setFullscreen(isFullscreen);
            if (!isFullscreen) {
                mapRef.current.setMinZoom(props.minZoom);
            }
        }

    }

    return <div className={'maplibregl-container'} ref={containerRef}>
        <div className={'maplibregl-map-container'}>
            <div
                className={'maplibregl-toggle maplibregl-fullscreen-toggle ' + (fullscreen ? 'maplibregl-layer-fullscreen-enabled' : 'maplibregl-fullscreen-toggle-disabled')}
                onClick={() => {
                    switchFullscreen();
                }}>
                <Fullscreen/>
            </div>
            {getUniqueAndVisibleLabelCategories().length &&
                <div
                    className={'maplibregl-toggle maplibregl-layer-toggle ' + (showLayer ? 'maplibregl-layer-toggle-enabled' : 'maplibregl-layer-toggle-disabled')}
                    onClick={() => {
                        setShowLayer(!showLayer);
                    }}>
                    <Layers/>
                </div>
            }
            <div className="maplibregl-layer" style={{visibility: (showLayer ? 'visible' : 'hidden'), opacity: (showLayer ? 1 : 0)}}>

                {getUniqueAndVisibleLabelCategories().map((category, index) => {
                    let enabled = 0;
                    let disabled = 0;

                    return <div key={index}>
                        <h5 className={'maplibregl-h5'}>{category}</h5>

                        {getUniqueLabelsSortedByCategoryAndVisible(category).map((label, index) => {

                            if (label.enabled) {
                                enabled++;
                            } else {
                                disabled++;
                            }

                            return <button key={index} title={label.title}
                                           className={'maplibregl-layer-button ' + (label.enabled ? 'maplibregl-layer-button-enabled' : 'maplibregl-layer-button-disabled')}
                                           onClick={() => {
                                               let uniqueLabelsSortedNew: MapUniqueLabel[] = uniqueLabelsSorted.map((item, i) => {
                                                   if (compareLabels(label, item) || label.title == 'Sonstige' && !item.visible) {
                                                       return {
                                                           title: item.title,
                                                           category: item.category,
                                                           enabled: !item.enabled,
                                                           visible: item.visible,
                                                           occurrence: item.occurrence
                                                       };
                                                   }
                                                   return item;
                                               })
                                               setUniqueLabelsSorted(uniqueLabelsSortedNew);
                                               setDisabledLabels(uniqueLabelsSortedNew.filter(item => !item.enabled).map(item => item));

                                           }}
                            >
                                <div className={'maplibregl-layer-button-content'}>
                                    <div className="maplibregl-layer-button-title">{label.title}</div>
                                    <div className="maplibregl-layer-button-occurrence">{label.occurrence}</div>
                                </div>
                            </button>
                        })}

                        {props.showBatchLayerButtonsIfMoreLabelsThan && (enabled + disabled > props.showBatchLayerButtonsIfMoreLabelsThan) &&
                            <div className={'maplibregl-link-deactivate'}>
                                {enabled > 0 &&
                                    <a className={'maplibregl-link '} onClick={() => batchChangeLabelStatus(false, category)}>Alle
                                        deaktivieren</a>
                                }
                                {disabled > 0 &&
                                    <a className={'maplibregl-link ' + (disabled == 0 ? 'maplibregl-link-disabled' : '')}
                                       onClick={() => batchChangeLabelStatus(true, category)}>Alle aktivieren</a>
                                }
                            </div>
                        }
                    </div>

                })}

            </div>
            <div className={"maplibregl-map " + (fullscreen ? "maplibregl-map-fullscreen" : "")}
                 style={{
                     width: '100%', minHeight: props.minHeight, maxHeight: fullscreen ? '100%' : props.maxHeight,
                     height: fullscreen ? '100%' : props.height
                 }}
                 id={mapKey}></div>
        </div>
        <div className="maplibregl-legend">
            {props.legend && props.legend.map((legend, index) => <div key={index} className={'maplibregl-legend-item'}>
                <div className={'maplibregl-legend-item-style'}>{createReactElementFromHTMLElement(mapMarkerContainer([legend.style]))}</div>
                <div className={'maplibregl-legend-item-label'} title={legend.label}>{legend.label}</div>
            </div>)}
        </div>
    </div>;
}

Map.defaultProps = {
    center: {
        lng: 12.969,
        lat: 48.838
    },
    mapMarkers: [],
    minHeight: '500px',
    defaultZoom: 5,
    minZoom: 4,
    maxZoom: 17,
    showLocationSearch: false,
    minLabelOccurrenceForLabelFilter: 1,
    showBatchLayerButtons: false,
};